import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { livingTrustPreferencesService } from '../../../../services/livingTrustPreferencesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('LivingTrustPreferencesInput');

type LivingTrustPreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'LivingTrustPreferencesInput'>;

export const LivingTrustPreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<LivingTrustPreferencesInputScreenRouteProp>();

  const { livingTrustPreferencesData, isEditing } = route.params || {};

  const [showNotifiedModal, setShowNotifiedModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helper function to format date input as MM/DD/YYYY
  const formatDate = (text: string) => {
    // Remove all non-digit characters
    const cleaned = text.replace(/\D/g, '');

    // Apply MM/DD/YYYY formatting
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    } else if (cleaned.length <= 8) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) + '/' + cleaned.slice(4, 8);
    }

    // Limit to 8 digits (MMDDYYYY)
    return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) + '/' + cleaned.slice(4, 8);
  };

  // Helper function to validate email format
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to validate date format
  const validateDate = (date: string) => {
    if (!date) return true; // Allow empty dates

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(date)) return false;

    // Additional validation: check if date is valid
    const [month, day, year] = date.split('/').map(Number);
    const dateObj = new Date(year, month - 1, day);
    return dateObj.getFullYear() === year &&
           dateObj.getMonth() === month - 1 &&
           dateObj.getDate() === day;
  };
  const [hasLivingTrust, setHasLivingTrust] = useState(
    livingTrustPreferencesData?.hasLivingTrust || false
  );
  const [hasLivingWill, setHasLivingWill] = useState(
    livingTrustPreferencesData?.hasLivingWill || false
  );
  const [formData, setFormData] = useState({
    trustLastUpdated: livingTrustPreferencesData?.trustLastUpdated || '',
    trustLocation: livingTrustPreferencesData?.trustLocation || '',
    trustExecutor: livingTrustPreferencesData?.trustExecutor || '',
    trustExecutorEmail: livingTrustPreferencesData?.trustExecutorEmail || '',
    trustExecutorNotified: livingTrustPreferencesData?.trustExecutorNotified || '',
    trustAdditionalDetails: livingTrustPreferencesData?.trustAdditionalDetails || '',
    // Living Will fields
    willLastUpdated: livingTrustPreferencesData?.willLastUpdated || '',
    willLocation: livingTrustPreferencesData?.willLocation || '',
    willExecutor: livingTrustPreferencesData?.willExecutor || '',
    willExecutorEmail: livingTrustPreferencesData?.willExecutorEmail || '',
    willExecutorNotified: livingTrustPreferencesData?.willExecutorNotified || '',
    willAdditionalDetails: livingTrustPreferencesData?.willAdditionalDetails || '',
  });

  const [emailErrors, setEmailErrors] = useState({
    trustExecutorEmail: false,
    willExecutorEmail: false,
  });

  const handleSubmit = async () => {
    // Validate dates
    if (formData.trustLastUpdated && !validateDate(formData.trustLastUpdated)) {
      Alert.alert('Invalid Date', 'Please enter a valid date for Trust last updated (MM/DD/YYYY)');
      return;
    }

    if (formData.willLastUpdated && !validateDate(formData.willLastUpdated)) {
      Alert.alert('Invalid Date', 'Please enter a valid date for Will last updated (MM/DD/YYYY)');
      return;
    }

    // Validate email addresses
    if (formData.trustExecutorEmail && !validateEmail(formData.trustExecutorEmail)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address for Trust executor');
      return;
    }

    if (formData.willExecutorEmail && !validateEmail(formData.willExecutorEmail)) {
      Alert.alert('Invalid Email', 'Please enter a valid email address for Will executor');
      return;
    }

    try {
      setSaving(true);

      const livingTrustUpdateData = {
        hasLivingTrust,
        hasLivingWill,
        trustLastUpdated: formData.trustLastUpdated || undefined,
        trustLocation: formData.trustLocation || undefined,
        trustExecutor: formData.trustExecutor || undefined,
        trustExecutorEmail: formData.trustExecutorEmail || undefined,
        trustExecutorNotified: formData.trustExecutorNotified || undefined,
        trustAdditionalDetails: formData.trustAdditionalDetails || undefined,
        willLastUpdated: formData.willLastUpdated || undefined,
        willLocation: formData.willLocation || undefined,
        willExecutor: formData.willExecutor || undefined,
        willExecutorEmail: formData.willExecutorEmail || undefined,
        willExecutorNotified: formData.willExecutorNotified || undefined,
        willAdditionalDetails: formData.willAdditionalDetails || undefined,
      };

      let savedLivingTrustPreferences;

      if (isEditing && livingTrustPreferencesData?.id) {
        // Update existing record
        savedLivingTrustPreferences = await livingTrustPreferencesService.updateLivingTrustPreferences(
          livingTrustPreferencesData.id,
          livingTrustUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedLivingTrustPreferences?.data || savedLivingTrustPreferences;
        logger.debug('Living Trust preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('LivingTrustPreferencesLockUnlock', {
          livingTrustPreferencesId: updatedData.id
        });
      } else {
        // Create new record
        savedLivingTrustPreferences = await livingTrustPreferencesService.createLivingTrustPreferences(livingTrustUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedLivingTrustPreferences?.data || savedLivingTrustPreferences;
        logger.debug('Living Trust preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('LivingTrustPreferencesLockUnlock', {
          livingTrustPreferencesId: createdData.id
        });
      }
    } catch (error) {
      logger.error('Error saving Living Trust preferences:', error);
      Alert.alert('Error', 'Failed to save Living Trust preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNotifiedSelect = (value: string, field: 'trustExecutorNotified' | 'willExecutorNotified') => {
    setFormData({ ...formData, [field]: value });
    setShowNotifiedModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Enter your Trust and or Will information</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Living Trust Toggle */}
          <View style={styles.toggleContainer}>
            <Text style={styles.toggleLabel}>Do you have a living trust?</Text>
            <TouchableOpacity
              style={[styles.toggleButton, hasLivingTrust && styles.toggleButtonActive]}
              onPress={() => setHasLivingTrust(!hasLivingTrust)}
            >
              <View style={[styles.toggleCircle, hasLivingTrust && styles.toggleCircleActive]} />
            </TouchableOpacity>
          </View>

          {/* Living Will Toggle */}
          <View style={styles.toggleContainer}>
            <Text style={styles.toggleLabel}>Do you have a living will?</Text>
            <TouchableOpacity
              style={[styles.toggleButton, hasLivingWill && styles.toggleButtonActive]}
              onPress={() => setHasLivingWill(!hasLivingWill)}
            >
              <View style={[styles.toggleCircle, hasLivingWill && styles.toggleCircleActive]} />
            </TouchableOpacity>
          </View>

          {/* Trust Information Section */}
          {hasLivingTrust && (
            <>
              <Text style={styles.sectionTitle}>Trust Information</Text>
              
              <View style={styles.inputGroup}>
                <Text style={styles.label}>When was your Trust last updated?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustLastUpdated}
                  onChangeText={(text) => {
                    const formattedDate = formatDate(text);
                    setFormData({ ...formData, trustLastUpdated: formattedDate });
                  }}
                  placeholder="MM/DD/YYYY"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="numeric"
                  maxLength={10}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Where is the original copy of your Trust stored?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustLocation}
                  onChangeText={(text) => setFormData({ ...formData, trustLocation: text })}
                  placeholder="Ex: Safety Deposit Box"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Who is the executor named in your Trust?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.trustExecutor}
                  onChangeText={(text) => setFormData({ ...formData, trustExecutor: text })}
                  placeholder="Ex: John Smith"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Email of Trust executor</Text>
                <TextInput
                  style={[
                    styles.input,
                    emailErrors.trustExecutorEmail && formData.trustExecutorEmail && styles.inputError
                  ]}
                  value={formData.trustExecutorEmail}
                  onChangeText={(text) => {
                    setFormData({ ...formData, trustExecutorEmail: text });
                    // Validate email in real-time
                    if (text && !validateEmail(text)) {
                      setEmailErrors({ ...emailErrors, trustExecutorEmail: true });
                    } else {
                      setEmailErrors({ ...emailErrors, trustExecutorEmail: false });
                    }
                  }}
                  placeholder="Ex: john.smith@email.com"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {emailErrors.trustExecutorEmail && formData.trustExecutorEmail && (
                  <Text style={styles.errorText}>Please enter a valid email address</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Has the Trust executor been notified?</Text>
                <TouchableOpacity
                  style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                  onPress={() => setShowNotifiedModal(true)}
                >
                  <Text style={[
                    { fontSize: 16, color: '#1C2C4F' },
                    !formData.trustExecutorNotified && { color: '#A9A9A9' }
                  ]}>
                    {formData.trustExecutorNotified || 'Select Yes/No'}
                  </Text>
                  <Image
                    source={require('../../../../assets/icons/down.png')}
                    style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Additional Trust details</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.trustAdditionalDetails}
                  onChangeText={(text) => setFormData({ ...formData, trustAdditionalDetails: text })}
                  placeholder="Enter any additional details"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>
            </>
          )}

          {/* Living Will Section - Conditional Render */}
          {hasLivingWill && (
            <>
              <Text style={[styles.sectionTitle, { marginTop: 30 }]}>Living Will Information</Text>
              
              <View style={styles.inputGroup}>
                <Text style={styles.label}>When was your Will last updated?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.willLastUpdated}
                  onChangeText={(text) => {
                    const formattedDate = formatDate(text);
                    setFormData({ ...formData, willLastUpdated: formattedDate });
                  }}
                  placeholder="MM/DD/YYYY"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="numeric"
                  maxLength={10}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Where is the original copy of your Will stored?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.willLocation}
                  onChangeText={(text) => setFormData({ ...formData, willLocation: text })}
                  placeholder="Ex: Safety Deposit Box"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Who is the executor named in your Will?</Text>
                <TextInput
                  style={styles.input}
                  value={formData.willExecutor}
                  onChangeText={(text) => setFormData({ ...formData, willExecutor: text })}
                  placeholder="Ex: John Smith"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Email of Will executor</Text>
                <TextInput
                  style={[
                    styles.input,
                    emailErrors.willExecutorEmail && formData.willExecutorEmail && styles.inputError
                  ]}
                  value={formData.willExecutorEmail}
                  onChangeText={(text) => {
                    setFormData({ ...formData, willExecutorEmail: text });
                    // Validate email in real-time
                    if (text && !validateEmail(text)) {
                      setEmailErrors({ ...emailErrors, willExecutorEmail: true });
                    } else {
                      setEmailErrors({ ...emailErrors, willExecutorEmail: false });
                    }
                  }}
                  placeholder="Ex: john.smith@email.com"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {emailErrors.willExecutorEmail && formData.willExecutorEmail && (
                  <Text style={styles.errorText}>Please enter a valid email address</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Has the Will executor been notified?</Text>
                <TouchableOpacity
                  style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                  onPress={() => setShowNotifiedModal(true)}
                >
                  <Text style={[
                    { fontSize: 16, color: '#1C2C4F' },
                    !formData.willExecutorNotified && { color: '#A9A9A9' }
                  ]}>
                    {formData.willExecutorNotified || 'Select Yes/No'}
                  </Text>
                  <Image
                    source={require('../../../../assets/icons/down.png')}
                    style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Additional Will details</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.willAdditionalDetails}
                  onChangeText={(text) => setFormData({ ...formData, willAdditionalDetails: text })}
                  placeholder="Enter any additional details"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>
            </>
          )}

          <TouchableOpacity
            style={[styles.submitButton, saving && { opacity: 0.7 }]}
            onPress={handleSubmit}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <Modal
        visible={showNotifiedModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowNotifiedModal(false)}
        statusBarTranslucent
        hardwareAccelerated={true}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setShowNotifiedModal(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Have they been notified?</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowNotifiedModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              <TouchableOpacity
                style={styles.stateItem}
                onPress={() => handleNotifiedSelect('Yes', 'trustExecutorNotified')}
              >
                <Text style={styles.stateItemText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.stateItem}
                onPress={() => handleNotifiedSelect('No', 'trustExecutorNotified')}
              >
                <Text style={styles.stateItemText}>No</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    backgroundColor: '#FFFFFF',
    padding: ms(15),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  toggleButton: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#d8dbdd',
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#29BFC0',
  },
  toggleCircle: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
  },
  toggleCircleActive: {
    transform: [{ translateX: 20 }],
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#F2994A',
    marginBottom: ms(20),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(30),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputError: {
    borderColor: '#FF4444',
    borderWidth: ms(2),
  },
  errorText: {
    color: '#FF4444',
    fontSize: ms(14, 0.3),
    marginTop: ms(5),
  },
});